/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import { type SuggestionItemResponse } from '@atlassian/navigation-bff-data-provider';
import { ATI_PRESETS, type UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import type { SuggestionConfigContext } from '../../../types';
import { getCreateContentLoader } from '../../integrations/forms/formLoader';

import { LinkItem } from './primitives/LinkItem/LinkItem';
import { LoomVideoItem } from './primitives/LoomItem/LoomVideoItem';
import { SuggestionItemBase } from './SuggestionItemBase';

export interface SuggestionItemProps {
	/**
	 * Additional context for the component
	 */
	context?: SuggestionConfigContext;

	/**
	 * The index of this item within the suggestion section (0-based)
	 */
	itemIndex: number;

	/**
	 * The source product context
	 */
	product: UniversalCreateSourceProduct;

	/**
	 * The response item from Universal Create
	 */
	responseItem: SuggestionItemResponse;

	/**
	 * The total count of items in the suggestion section
	 */
	totalItemsCount: number;
}

function renderPlatformSuggestionItem({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
}: {
	context: SuggestionConfigContext;
	itemIndex: number;
	product: UniversalCreateSourceProduct;
	responseItem: SuggestionItemResponse;
	totalItemsCount: number;
}) {
	switch (responseItem.ati) {
		case ATI_PRESETS.LOOM:
			return (
				<LoomVideoItem
					responseItem={responseItem}
					context={context}
					product={product}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
				/>
			);
		case ATI_PRESETS.GOAL:
		case ATI_PRESETS.PROJECT:
		case ATI_PRESETS.TEAM: {
			if (!fg('navx_uc_platformisation_forms_enabled')) {
				return null; // should not show this item if the feature flag is off
			}

			const loadCreateContent = getCreateContentLoader(responseItem.ati);

			const customisedItemConfig = {
				loadCreateContent,
				onClickCloseMenu: !loadCreateContent,
			};

			return (
				<SuggestionItemBase
					responseItem={responseItem}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
					customisedItemConfig={customisedItemConfig}
				/>
			);
		}
		default:
			return undefined;
	}
}

function renderSuggestionItem({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
}: {
	context: SuggestionConfigContext;
	itemIndex: number;
	product: UniversalCreateSourceProduct;
	responseItem: SuggestionItemResponse;
	totalItemsCount: number;
}) {
	switch (responseItem.type) {
		case 'platform':
			return renderPlatformSuggestionItem({
				responseItem,
				itemIndex,
				totalItemsCount,
				context,
				product,
			});
		case 'link':
			return (
				// eslint-disable-next-line @atlassian/a11y/anchor-is-valid, @atlassian/a11y/anchor-has-content -- See https://go/a11y-anchor-has-content for more details
				<LinkItem
					responseItem={responseItem}
					context={context}
					product={product}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
				/>
			);
		default:
			break;
	}

	return undefined;
}

function SuggestionItemInner({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
}: SuggestionItemProps) {
	if (context) {
		const renderedItem = renderSuggestionItem({
			responseItem,
			itemIndex,
			totalItemsCount,
			context,
			product,
		});

		if (renderedItem !== undefined) {
			return renderedItem;
		}
	}

	// Return general suggestion item with no customisedItemConfig
	return (
		<SuggestionItemBase
			responseItem={responseItem}
			itemIndex={itemIndex}
			totalItemsCount={totalItemsCount}
		/>
	);
}

/**
 * A component that renders a suggestion item.
 * Wrapped by the ErrorBoundary component to handle errors so that it won't break the whole menu.
 *
 * @param props - @see SuggestionItemProps
 * @returns - JSX.Element @see SuggestionItemInner
 */
export function SuggestionItem(props: SuggestionItemProps) {
	return (
		<ErrorBoundary fallback={null}>
			<SuggestionItemInner {...props} />
		</ErrorBoundary>
	);
}
